/* eslint-disable import/no-anonymous-default-export */

import { convertDateStringsToTimestamps } from "../../../../db/schema/schemaHooks";
import { createId, isValidCuid2 } from "../../../../utils/cuid2Utils";
import {
  timezoneOffsets,
  convertIsoOffsetToUTC,
} from "../../../../utils/dateUtils";

/**
 * database configuration
 * ----------------------------------------
 * Format matches feature config modules so it is auto-collected by your existing
 * Webpack loader (require.context) alongside other feature configs.
 *
 * Version semantics (store.object.data): "MAJOR.MINOR.PATCH"
 * - MAJOR (store): Dexie store definition change → requires DB version bump.
 * - MINOR (object): object model changes → coerce existing records.
 * - PATCH (data): data-only adjustments (upserts) → no schema change.
 */

const VERSION = "1.1.1";

/**
 * Default store object model for settings.
 *
 *  * Update level must be MINOR for model changes to take effect
 *
 * Adding keys to data objects will occur when the key is added to this model
 * Removing keys from data objects requires using the keyPruning function in the transform hook
 *
 * @constant {Object} modelStore - represents the store and may be the parent of all data objects.
 *
 * @property {string} id - Unique identifier for the store. Must be set to the store's name.
 * @property {string} label -Human-readable label for the store which may be used in UI
 * @property {string} type - Type identifier for the store
 * @property {string} description -Longer description or help text explaining the purpose of the store.
 * @property {Array.<{id:number,label:string,order:number}>} categories
 *   Array of category objects available to this store. Each category has:
 *     - id {number}: numeric category identifier
 *     - label {string}: human-readable category label
 *     - order {number}: numeric ordering index for categories
 *
 *   Categories are used to group settings and Labels are used in UI
 *
 * @property {string|null} image - URL or path to an image associated with the setting, or null.
 * @property {string} version - Semantic/version string representing the version of this store definition.
 * @property {number} created - Creation timestamp in milliseconds since the epoch (Date.now()).
 * @property {number} modified - Last-modified timestamp in milliseconds since the epoch (Date.now()).
 */
export const modelStore = {
  id: "", // must be set to stores name
  label: "",
  type: "",
  description: "",
  categories: [{ id: 0, label: "", order: 0 }],
  image: "",
  version: "", // version of this store
  created: Date.now(),
  modified: Date.now(),
};

/**
 * Default model for data entry.
 *
 * Update level must be MINOR for model changes to take effect
 *
 * Adding keys to data objects will occur when the key is added to this model
 * Removing keys from data objects requires using the keyPruning function in the transform hook
 *
 * @constant {Object} modelData
 * @property {string} id - CUID2 Unique identifier for the setting
 * @property {string} title - Title of the tale
 * @property {number} startDate - Start timestamp of the tale
 * @property {number} endDate - End timestamp of the tale
 * @property {string} description - Description of the tale
 * @property {string} timeZone - Primary TimeZone abbreviation of the tale
 * @property {number} created - Creation timestamp in milliseconds since epoch (Date.now()).
 * @property {number} modified - Last-modified timestamp in milliseconds since epoch (Date.now()).
 */
export const modelData = {
  type: "",
  // subType: "",
  // confirmation: "",
  // providerCode: "",
  // providerName: "",
  // originCode: "",
  // destinationCode: "",
  // originName: "",
  // destinationName: "",
  // departureDate: "2024-02-06",
  // arrivalDate: "2024-02-06",
  // departureTime: "06:30",
  // arrivalTime: "08:37",
  providerName: "",
  contacts: {
    address: "",
    phone: "",
    email: "",
    web: "",
  },
  coordinates: {
    // departure: {
    //   accuracy: 0.0,
    //   altitude: 0.0,
    //   heading: 0.0,
    //   longitude: 0.0,
    //   latitude: 0.0,
    //   speed: 0.0,
    // },
    // arrival: {
    //   accuracy: 0.0,
    //   altitude: 0.0,
    //   heading: 0.0,
    //   longitude: 0.0,
    //   latitude: 0.0,
    //   speed: 0,
    // },
  },
  links: {},
  // passengers: [
  //   {
  //     firstName: "",
  //     lastName: "",
  //     seatNumber: "",
  //   },
  // ],
  id: "",
  taleId: "",
  images: [],
  tags: [],
  created: Date.now(),
  updated: Date.now(),
};

// #region
/**
 * Migration Configuration Object
 *
 * Defines a single migration step for a store, including version information,
 * schema changes, data updates, and transformation hooks. Each configuration
 * file represents one version increment (MAJOR, MINOR, or PATCH).
 *
 * @typedef {Object} MigrationConfig
 *
 * ============================================================================
 * VERSION MANAGEMENT
 * ============================================================================
 *
 * @property {string} version
 *   REQUIRED. Semantic version string (MAJOR.MINOR.PATCH).
 *   Must match the version in the config file name.
 *
 *   Versioning Rules:
 *   - New stores start at 1.1.1
 *   - PATCH (position 3): Data object key values (1.1.1 -> 1.1.2)
 *   - MINOR (position 2): Model updates (1.1.x -> 1.2.1)
 *   - MAJOR (position 1): Store schema updates (1.x.x -> 2.1.1)
 *
 *   Constraints:
 *   - Cannot skip versions (1.1.1 cannot jump to 1.1.5)
 *   - After incrementing MINOR, PATCH resets to 1
 *   - After incrementing MAJOR, both MINOR and PATCH reset to 1
 *
 *   Examples:
 *   - 1.1.1 -> 1.1.2 ✓ (patch increment)
 *   - 1.1.2 -> 1.2.1 ✓ (minor increment, patch resets)
 *   - 1.2.7 -> 2.1.1 ✓ (major increment, both reset)
 *   - 1.1.1 -> 1.1.5 ✗ (skips versions 1.1.2-1.1.4)
 *   - 1.1.2 -> 1.2.2 ✗ (patch should reset to 1)
 *
 * @property {boolean} forceUpdate
 *   Controls whether the user can skip this migration.
 *   - true: Skip user prompt, apply update automatically
 *   - false: Allow user to decline the update
 *
 *   Future Behavior:
 *   When true, will prompt user to backup before forcing the update.
 *
 * @property {string} type
 *   Human-readable update type indicator: "MAJOR" | "MINOR" | "PATCH"
 *   For convenience and quick reference only.
 *   Not used in code to determine update behavior.
 *
 * ============================================================================
 * DOCUMENTATION
 * ============================================================================
 *
 * @property {string[]} changeLog
 *   Array of specific technical changes included in this config.
 *   Intended for developer documentation and internal tracking.
 *
 *   Examples:
 *   - "Removing description index"
 *   - "Adding email field to user model"
 *   - "Migrating data from oldField to newField"
 *
 * @property {string} changeDescription
 *   User-facing description of the update.
 *   Displayed to users when prompted to apply the migration.
 *   Should be clear and non-technical.
 *
 *   Example:
 *   "This update improves search performance by optimizing the index structure."
 *
 * ============================================================================
 * STORE SCHEMA (MAJOR UPDATES)
 * ============================================================================
 *
 * @property {Object} stores
 *   Defines the IndexedDB store schema and indexes.
 *
 *   Rules:
 *   - Only ONE store definition per config file
 *   - Additional stores will be ignored
 *   - Store modifications require a MAJOR version bump
 *   - Modifying indexes (add/remove) requires a new MAJOR update config
 *
 *   Format:
 *   The key is the store name, the value is a comma-separated string of indexes.
 *   First index is the keyPath, remaining are indexed fields.
 *
 *   @example
 *   stores: {
 *     settings: "id,modified,category"
 *     // "id" = keyPath
 *     // "modified" and "category" = indexed fields for queries
 *   }
 *
 *   Index Modifications:
 *   - Add index: Include new index in string, create MAJOR config
 *   - Remove index: Exclude index from string, create MAJOR config
 *
 * ============================================================================
 * MODEL UPDATES (MINOR UPDATES)
 * ============================================================================
 *
 * @property {Object} model
 *   Defines the data models for store objects and data objects.
 *
 * @property {Object} model.storeObject
 *   Model for the single store metadata object.
 *   This is the special object with id "__history" that tracks store state.
 *
 *   Model Modifications:
 *   - Add keys: Include new key in model, create MINOR config
 *   - Remove keys: Remove key from model, create MINOR config
 *
 * @property {Object} model.dataObject
 *   Model for all data objects in the store (excluding storeObject).
 *   All data rows will conform to this model.
 *
 *   Model Modifications:
 *   - Add keys: Include new key in model, create MINOR config
 *   - Remove keys: Remove key from model AND configure transform hook
 *     with keyPruning to remove the keys from existing data
 *     (keys are not removed automatically)
 *
 *   @example
 *   model: {
 *     storeObject: {
 *       id: "__history",
 *       version: "1.2.1",
 *       created: Date.now(),
 *       modified: Date.now()
 *     },
 *     dataObject: {
 *       id: "",
 *       name: "",
 *       email: "", // newly added field
 *       created: Date.now(),
 *       modified: Date.now()
 *     }
 *   }
 *
 * ============================================================================
 * DATA POPULATION (PATCH UPDATES)
 * ============================================================================
 *
 * @property {Array} data
 *   Array of data objects to add to the store during this update.
 *   Empty array if no data needs to be added.
 *
 *   Initial Store Setup (v1.1.1):
 *   - Optionally seed the storeObject to lock starting versions
 *   - If not seeded, fallback creates object from model with version
 *     and timestamps
 *
 *   Each object in the array should conform to the dataObject model.
 *
 *   @example
 *   data: [
 *     {
 *       id: "default-1",
 *       name: "Default Setting",
 *       value: true,
 *       created: 1699564800000,
 *       modified: 1699564800000
 *     }
 *   ]
 *
 * ============================================================================
 * TRANSFORMATION HOOKS
 * ============================================================================
 *
 * @property {Object} hooks
 *   Optional transformation and lifecycle hooks.
 *   Hooks apply to data objects only, never to the storeObject/meta row.
 *
 * @property {Function} [hooks.transform]
 *   Transforms each data object during migration.
 *
 *   @param {Object} obj - The data object being transformed
 *   @param {Object} ctx - Context object
 *   @param {string} ctx.stepVersion - The version of this migration step
 *   @param {Object} ctx.model - The target dataObject model
 *   @param {Object} ctx.utils - Utility functions
 *   @param {Function} ctx.utils.keyPruning - Removes keys not in model
 *   @returns {Object} The transformed object
 *
 *   Common Use Cases:
 *   - Remove deprecated fields with keyPruning
 *   - Transform field values
 *   - Migrate data from old structure to new
 *
 *   @example
 *   transform: (obj, ctx) => {
 *     console.debug(`[settings@${ctx.stepVersion}] transform`, Object.keys(obj));
 *
 *     // Remove fields no longer in the model
 *     ctx.utils.keyPruning(obj, ctx.model);
 *
 *     // Transform a field
 *     if (obj.oldField) {
 *       obj.newField = obj.oldField.toUpperCase();
 *       delete obj.oldField;
 *     }
 *
 *     return obj;
 *   }
 *
 * @property {Function} [hooks.beforePopulate]
 *   Runs before data population begins.
 *
 *   @param {Object} params
 *   @param {IDBDatabase} params.db - IndexedDB database instance
 *   @param {string} params.store - Store name
 *   @param {string} params.version - Migration version
 *   @returns {Promise<void>}
 *
 *   Use Cases:
 *   - Prepare data structures
 *   - Validate preconditions
 *   - Set up temporary state
 *
 * @property {Function} [hooks.afterPopulate]
 *   Runs after data population completes.
 *
 *   @param {Object} params
 *   @param {IDBDatabase} params.db - IndexedDB database instance
 *   @param {string} params.store - Store name
 *   @param {string} params.version - Migration version
 *   @returns {Promise<void>}
 *
 *   Use Cases:
 *   - Clean up temporary data
 *   - Trigger post-migration actions
 *   - Validate migration success
 */
// #endregion
export default {
  /**
   * version  MAJOR.MINOR.PATCH
   *
   * For new store, set version at 1.1.1
   *
   * To apply patch data object key values, increment position 3 (1.1.2, so 1.1.1 -> 1.1.2)
   * To apply minor "model" updates, increment position 2 (1.2.1, so 1.1.1 || 1.1.2 || 1.1.6 -> 1.2.1)
   * To apply MAJOR "store" updates, increment postion 1 (2.1.1, so 1.2.1 || 1.2.7 || 1.9.4 -> 2.1.1)
   *
   * Versions cannot jump sequence
   *   (e.g. ver 1.1.1 cannot jump to 1.1.5, ver 1.1.2 cannot jump to 1.2.2, it must go to 1.2.1)
   *
   * @property {string} version - REQUIRED Version used in update and must match version in file name
   */
  version: VERSION,

  /**
   * forceUpdate
   *
   * to be used when updates are required not optional by user
   *
   * true  this will skip the user prompt
   * false this will allow user to skip update
   *
   * ** FUTURE behavior will be to prompt user to backup first and then force the update
   *
   * @property {boolean} forceUpdate - force update without allowing user to skip
   */
  forceUpdate: false,

  /**
   * type
   *
   * Type options (MAJOR || MINOR || PATCH)
   *
   * This value is for convenience and quick reference and is not used to determine any update decisions in code
   *
   * @property {string} type - Human-readable indication representing the version number update type MAJOR.MINOR.PATCH
   * */
  type: "MAJOR",

  /**
   * changeLog
   *
   * Intended for documentation purposes but could be used for other purposes as well
   *
   * @property {string[]} changeLog - Array of specific changes this config contains
   */
  changeLog: [
    "- create store and indexes",
    "- establish store and data models",
    "- populate store with initial settings",
  ],

  /**
   * changeDescription
   *
   * Description of changes in this config update. Intended to be informative to app user
   *  if prompted to update
   *
   * @property {string} changeDescription - Public description of update
   */
  changeDescription: "Initiate Segments store as part of TOAT feature.",

  /**
   * stores (MAJOR)
   *
   * ONLY supports a single store defined per config, new store = new config file
   *  additional configured stores will be ignored
   *
   * Store modifications require a database version bump
   *
   *  + to modify indexes (adding), add index configuration, create a MAJOR update config file
   *  - to modify indexes (removing), remove index, leaving other indexes in place, crate MAJOR update config file
   *
   * @constant {Object} stores Accepts only one store and schema definition
   */
  stores: {
    segments: "id,taleId,type,providerName",
  },

  /**
   * Models (MINOR)
   *
   * Minor updates will make configured change to ALL data objects
   * For storeObject
   *  + to modify model (adding keys), add key and create a MINOR update config file
   *  - to modify model (removing keys), remove key leaving other keys
   *
   * For dataObject
   *  + to modify model (adding keys), add key and create a MINOR update config file
   *  - to modify model (removing keys), remove key leaving other keys and configure
   *    transform hook to run keyPruning. Keys are not removed automatically
   *
   * @constant {Object} storeObject Defines model for single store object
   * @constant {Object} dataObject Defines model for all data objects
   */
  model: {
    storeObject: modelStore,
    dataObject: modelData,
  },

  dataPolicies: {
    id: {
      generate: "all", // keep || all ||  invalid (default=keep)
      //   action: ({ store, index }) => `${store}-${index + 1}`,
    },
  },
  /**
   * data (PATCH)
   *
   * When store is initiated (v1.1.1)
   * - Seed the storeObject row in data for this store if you want to lock their starting versions.
   * - Fallback will create an object using model and will only apply version and date/time stamps
   *
   * @property {Array.<{...dataObject}>} data define data items to add to store for this patch update
   *    if no data needs to be added, keep array empty
   */

  data: [
    {
      id: "segments", // must be set to stores name
      label: "Segments of tale",
      type: "feature",
      description: "Store for Segments of a Trip feature.",
      categories: [],
      image: null,
      version: VERSION,
      created: Date.now(),
      modified: Date.now(),
    },
    {
      type: "transportation",
      mode: "airplane",
      terms: "hire",
      confirmation: "DXC765",
      providerCode: "UA1782",
      providerName: "United",
      originCode: "BOI",
      destinationCode: "DEN",
      originName: "Boise, ID",
      destinationName: "Denver, CO",
      contacts: {
        address: "",
        phone: "",
        email: "",
        web: "",
      },
      coordinates: {
        departure: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -116.2248908,
          latitude: 43.565827,
          speed: 0,
        },
        arrival: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -104.7776748,
          latitude: 39.8563189,
          speed: 0,
        },
      },
      links: {
        status:
          "https://www.united.com/en/us/flightstatus/details/1782/2024-02-06/BOI/DEN/UA",
      },
      id: "n5fn0rmpwfotz7oaqfqg0w1v",
      taleId: "rwkntocyn9q73xuwux5aqd0g",
      created: 1707167500816,
      updated: 1747935114161,
      departureDateTime: "2024-02-06T13:30:00.000Z",
      departureTimezone: "America/Boise",
      arrivalDateTime: "2024-02-06T15:37:00.000Z",
      arrivalTimezone: "America/Denver",
    },
    {
      type: "transportation",
      mode: "airplane",
      terms: "hire",
      confirmation: "DXC765",
      providerCode: "UA1917",
      providerName: "United",
      originCode: "DEN",
      destinationCode: "LIR",
      originName: "Denver, CO",
      destinationName: "Liberia, CR",
      contacts: {
        address: "",
        phone: "",
        email: "",
        web: "",
      },
      coordinates: {
        departure: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -104.7776748,
          latitude: 39.8563189,
          speed: 0,
        },
        arrival: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -85.5406712,
          latitude: 10.6006094,
          speed: 0,
        },
      },
      links: {
        status:
          "https://www.united.com/en/us/flightstatus/details/1917/2024-02-06/DEN/LIR/UA",
      },
      id: "i2fbecrq05zd9787j5flccga",
      taleId: "rwkntocyn9q73xuwux5aqd0g",
      created: 1707167500829,
      updated: 1747935114161,
      departureDateTime: "2024-02-06T17:03:00.000Z",
      departureTimezone: "America/Denver",
      arrivalDateTime: "2024-02-06T22:08:00.000Z",
      arrivalTimezone: "America/Costa_Rica",
    },
    {
      type: "transportation",
      mode: "airplane",
      terms: "hire",
      confirmation: "DXC765",
      providerCode: "UA1918",
      providerName: "United",
      originCode: "LIR",
      destinationCode: "DEN",
      originName: "Liberia, CR",
      destinationName: "Denver, CO",
      contacts: {
        address: "",
        phone: "",
        email: "",
        web: "",
      },
      coordinates: {
        departure: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -85.5406712,
          latitude: 10.6006094,
          speed: 0,
        },
        arrival: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -104.7776748,
          latitude: 39.8563189,
          speed: 0,
        },
      },
      links: {
        status:
          "https://www.united.com/en/us/flightstatus/details/1918/2024-02-16/LIR/DEN/UA",
      },
      id: "xz3ywo8fuvi6uv9mb1jci6ac",
      taleId: "rwkntocyn9q73xuwux5aqd0g",
      created: 1707167500835,
      updated: 1747935114161,
      departureDateTime: "2024-02-16T18:50:00.000Z",
      departureTimezone: "America/Costa_Rica",
      arrivalDateTime: "2024-02-17T00:25:00.000Z",
      arrivalTimezone: "America/Denver",
    },
    {
      type: "transportation",
      mode: "airplane",
      terms: "hire",
      confirmation: "DXC765",
      providerCode: "UA367",
      providerName: "United",
      originCode: "DEN",
      destinationCode: "BOI",
      originName: "Denver, CO",
      destinationName: "Boise, ID",
      contacts: {
        address: "",
        phone: "",
        email: "",
        web: "",
      },
      coordinates: {
        departure: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -104.7776748,
          latitude: 39.8563189,
          speed: 0,
        },
        arrival: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -116.2248908,
          latitude: 43.565827,
          speed: 0,
        },
      },
      links: {
        status:
          "https://www.united.com/en/us/flightstatus/details/367/2024-02-16/DEN/BOI/UA",
      },
      id: "bugtzlt7ikq0d4i28lm5ph1x",
      taleId: "rwkntocyn9q73xuwux5aqd0g",
      created: 1707167500842,
      updated: 1747935114162,
      departureDateTime: "2024-02-17T02:20:00.000Z",
      departureTimezone: "America/Denver",
      arrivalDateTime: "2024-02-17T04:21:00.000Z",
      arrivalTimezone: "America/Boise",
    },
    {
      type: "accommodation",
      spaceType: "hotel",
      terms: "rent",
      providerName: "Heliconias Rainforest Lodge",
      contacts: {
        address: "3 Km Sur Oeste Del Liceo, Bijagua, 11000, Costa Rica",
        phone: "+506 2466 8483",
        email: "heliconiaslodge@gmail.com",
        web: "https://heliconiascr.com/",
      },
      coordinates: {
        accuracy: "",
        altitude: "",
        heading: null,
        longitude: -85.0411016,
        latitude: 10.7135858,
        speed: 0,
      },
      links: {
        reservation: "https://shrtr.io/ZDc1ZmI0Y",
      },
      booking: {
        confirmation: "4055194514",
        totalAmount: "345.00",
        currency: "USD",
        cancellation: "until 22 January 2024 23:59: US $0",
        details: "",
        roomType: "1 Bungalow",
        specialRequests: "",
        services: ["laundry", "breakfast", "parking", "refrigerator"],
      },
      id: "vperyukwd27jfg3fgbd6j36z",
      taleId: "rwkntocyn9q73xuwux5aqd0g",
      created: 1707167500847,
      updated: 1747935114162,
      checkInDateTime: "2024-02-06T20:00:00.000Z",
      checkOutDateTime: "2024-02-08T17:00:00.000Z",
      timezone: "America/Costa_Rica",
    },
    {
      type: "accommodation",
      spaceType: "hotel",
      terms: "rent",
      providerName: "Brisas del Arenal",
      contacts: {
        address: "La fortuna, calle 506, Cabañas Brisas Arenal 21007",
        phone: "+50688799225",
        email: "info@brisasarenal.com",
        web: "https://www.brisasarenal.com",
      },
      coordinates: {
        accuracy: "",
        altitude: "",
        heading: null,
        longitude: -84.6667887,
        latitude: 10.4539113,
        speed: 0,
      },
      links: {
        reservation: "https://shrtr.io/YTY0MzE2M",
      },
      booking: {
        confirmation: "3310729459106",
        totalAmount: "180.00",
        currency: "USD",
        cancellation:
          "3 days before arrival at the hotel to cancel their reservation without additional cost",
        details:
          "Reception hours from Monday to Saturday from 8:00 a.m. to 8:30 pm and Sunday from 9:00 am to 7 30 pm If you need more time please contact by email (info@brisasarenal.com) or by phone (24799225 or 83522430)\nBREAKFAST: Three meal times are offered 7:30 A.M./8:00 AM. /8:30 A.M. It is the client's responsibility to arrive on time (no breakfast will be given either before the first time, or near closing 8:50 a.m.or at closing time which is at 9:00 A.M., they are not backdated)The hotel offers 2 to 4different types of breakfast, you have the right to choose one of the breakfasts already established (we do not undertake to satisfy taste,preferences, quantity or needs outside the menu offered)",
        roomType: "Volcano view room (x1)",
        specialRequests: "",
        services: [
          "wifi",
          "laundry",
          "lounge",
          "business center",
          "room service",
          "front desk safe",
          "valet",
          "breakfast",
          "refrigerator",
          "parking",
        ],
      },
      id: "l353ogdfhr2yccshuzwc6gcp",
      taleId: "rwkntocyn9q73xuwux5aqd0g",
      created: 1707167500853,
      updated: 1747935114163,
      checkInDateTime: "2024-02-08T20:00:00.000Z",
      checkOutDateTime: "2024-02-10T17:00:00.000Z",
      timezone: "America/Costa_Rica",
    },
    {
      type: "accommodation",
      spaceType: "hotel",
      terms: "rent",
      providerName: "Living Hotel",
      contacts: {
        address: "Nosara, Nosara, 00000, Costa Rica",
        phone: "+506 7037 4231",
        email: "4148555174-zase.htfm.pe8t.e9bd@property.booking.com",
        web: "",
      },
      coordinates: {
        accuracy: "",
        altitude: "",
        heading: null,
        longitude: -85.670029,
        latitude: 9.950194,
        speed: 0,
      },
      links: {
        reservation: "https://shrtr.io/YTY0MzE2M",
      },
      booking: {
        confirmation: "4148555174",
        totalAmount: "621.50",
        currency: "USD",
        cancellation:
          "You can cancel for FREE until 26 January 2024 23:59 [CST].",
        details:
          "Private bathroom • Garden view • Pool view • Free toiletries • Inner courtyard view • Air conditioning • Safety deposit box • Bidet • Toilet • Sofa • Bath or shower • Towels • Linen • Socket near the bed • Tile/marble floor • Desk • Seating Area • Private entrance • Refrigerator • Mosquito net • Hairdryer • Fan • Dressing room • Patio • Laptop safe • Wardrobe or closet • Entire unit located on ground floor • Clothes rack • Toilet paper",
        roomType: "King Room",
        specialRequests: "",
        services: ["air conditioning", "room safe", "refrigerator", "parking"],
      },
      id: "llfe44nt2nhfuy1nwm84dbu4",
      taleId: "rwkntocyn9q73xuwux5aqd0g",
      created: 1707167500860,
      updated: 1747935114164,
      checkInDateTime: "2024-02-10T20:00:00.000Z",
      checkOutDateTime: "2024-02-12T17:00:00.000Z",
      timezone: "America/Costa_Rica",
    },
    {
      type: "accommodation",
      spaceType: "hotel",
      terms: "rent",
      providerName: "Punta Islita, Autograph Collection",
      contacts: {
        address: "Punta Islita Beach Guanacaste Costa Rica",
        phone: "+506-2656-3500",
        email: "",
        web: "https://www.marriott.com/en-us/hotels/lirak-punta-islita-autograph-collection/overview/",
      },
      coordinates: {
        accuracy: "",
        altitude: "",
        heading: null,
        longitude: -85.4009992,
        latitude: 9.8572922,
        speed: 0,
      },
      links: {
        reservation: "https://shrtr.io/ZWQ1N2RlZ",
      },
      booking: {
        confirmation: "96348785",
        totalAmount: "156,000",
        currency: "PTS",
        cancellation:
          "You may cancel your reservation for no charge before 11:59 PM local hotel time on February 5, 2024 (7 day[s] before arrival).",
        details: "",
        roomType: "Deluxe Guest room, 1 King, Ocean view, Whirlpool",
        specialRequests: "",
        services: ["wifi", "parking", "business center"],
      },
      id: "rsj40ogjz39xj48og4pvoqcu",
      taleId: "rwkntocyn9q73xuwux5aqd0g",
      created: 1707167500865,
      updated: 1747935114165,
      checkInDateTime: "2024-02-12T21:00:00.000Z",
      checkOutDateTime: "2024-02-15T18:00:00.000Z",
      timezone: "America/Costa_Rica",
    },
    {
      type: "accommodation",
      spaceType: "hotel",
      terms: "rent",
      providerName: "Hotel Colono Beach",
      booking: {
        confirmation: "72051597854353",
        totalAmount: "135.60",
        currency: "USD",
        cancellation:
          "Free cancellation until Feb 13 at 6:00pm (Central America Standard Time)",
        details: "",
        roomType: "Deluxe Room",
        specialRequests: "",
        services: [],
      },
      contacts: {
        address: "",
        phone: "",
        email: "",
        web: "https://www.hotels.com/ho525034/hotel-colono-beach-coco-costa-rica/?langid=1033",
      },
      coordinates: {
        accuracy: "",
        altitude: "",
        heading: null,
        longitude: -85.6973619,
        latitude: 10.548516,
        speed: 0,
      },
      links: {
        reservation: "https://maps.app.goo.gl/8T4c74KQfQTmET3B7",
      },
      id: "itb62d2oyyhx9gw2qe7tdemu",
      taleId: "rwkntocyn9q73xuwux5aqd0g",
      created: 1707167500871,
      updated: 1747935114165,
      checkInDateTime: "2024-02-15T20:00:00.000Z",
      checkOutDateTime: "2024-02-16T16:30:00.000Z",
      timezone: "America/Costa_Rica",
    },
    {
      type: "transportation",
      mode: "car",
      terms: "rent",
      providerName: "Adobe Rent-A-Car",
      confirmation: "1086954",
      carType: "Mitsubishi ASX AT 4WD or similar / Group B",
      pickupDateTime: "2024-02-06T22:30:00.000Z",
      returnDateTime: "2024-02-16T17:30:00.000Z",
      pickupLocation: "Aeropuerto Liberia [LIR]",
      returnLocation: "Aeropuerto Liberia [LIR]",
      pickupTimezone: "America/Costa_Rica",
      returnTimezone: "America/Costa_Rica",
      contacts: {
        address: "",
        phone: "+50625424890",
        email: "info@adobecar.com",
        web: "https://www.adobecar.cr",
      },
      coordinates: {
        pickup: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -85.6276238,
          latitude: 10.5537609,
          speed: 0,
        },
        return: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -85.6276238,
          latitude: 10.5537609,
          speed: 0,
        },
      },
      links: {},
      drivers: [
        {
          firstName: "David",
          lastName: "Broderick",
          licenseNumber: "",
          dob: "",
        },
        {
          firstName: "",
          lastName: "",
          licenseNumber: "",
          dob: "",
        },
      ],
      insurance: {
        type: "Full Coverage",
        providerName: "InsuranceCo",
        policyNumber: "XYZ98765",
      },
      totalAmount: "1184.10",
      currency: "USD",
      id: "vrgf5i1nqfhignh6vcv3n7yg",
      taleId: "rwkntocyn9q73xuwux5aqd0g",
      created: 1707167500877,
      updated: 1747935114166,
    },
    {
      type: "activity",
      activityType: "hiking",
      providerName: "Tenorio Volcano National Park",
      activityName: "Rio Celeste - Waterfall - Tenorio Volcano National Park",
      activityDetails:
        "There is no cell phone signal up near the park (at least with Kolbi) so make sure you download Google Maps offline. You will know when you’ve reached the park entrance because you will see a lot of signs for parking. There will be locals wearing yellow vests waving flags, trying to get you to park in their lot. We prefer to park at the parking lot right at the entrance (left side in the photo above).\n Parking costs ~2000 CRC per car (cash only) give to attendant and display slip of paper on the dash of the car.\n Even though you’re paying for parking, don’t leave valuables visible in the car as it’s technically not a secured lot and it is not owned by the national park. Roll up your windows and lock your doors.",
      meetingPoint: "",
      contacts: {
        address: "",
        phone: "",
        email: "",
        web: "",
      },
      coordinates: {
        start: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -84.990139,
          latitude: 10.7163273,
          speed: 0,
        },
        end: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -84.990139,
          latitude: 10.7163273,
          speed: 0,
        },
      },
      links: {
        web: "https://mytanfeet.com/activities/tips-visiting-rio-celeste/",
      },
      booking: {
        totalAmount: "12.00",
        currency: "USD",
        confirmation: "ABAACC",
        cancellation: "",
        details: "",
      },
      id: "tc7kapjtsq4shm6r8ee5yiak",
      taleId: "rwkntocyn9q73xuwux5aqd0g",
      created: 1707167500883,
      updated: 1747935114166,
      startDateTime: "2024-02-08T17:00:00.000Z",
      startTimezone: "America/Costa_Rica",
      endDateTime: "2024-02-08T21:30:00.000Z",
      endTimezone: "America/Costa_Rica",
    },
    {
      type: "activity",
      activityType: "lounging",
      terms: "free",
      providerName: "",
      activityName: "Lounging on the beach",
      activityDetails: "",
      meetingPoint: "Beach",
      contacts: {
        address: "",
        phone: "",
        email: "",
        web: "",
      },
      coordinates: {
        start: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -85.401261,
          latitude: 9.852942,
          speed: 0,
        },
        end: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -85.401261,
          latitude: 9.852942,
          speed: 0,
        },
      },
      links: {},
      booking: {
        totalAmount: "0.00",
        currency: "USD",
        confirmation: "",
        cancellation: "",
        details: "",
      },
      id: "mmvr8qp62miwo7p25krmprok",
      taleId: "rwkntocyn9q73xuwux5aqd0g",
      created: 1707167500889,
      updated: 1747935114166,
      startDateTime: "2024-02-14T16:00:00.000Z",
      startTimezone: "America/Costa_Rica",
      endDateTime: "2024-02-14T21:00:00.000Z",
      endTimezone: "America/Costa_Rica",
    },
    {
      type: "activity",
      activityType: "walking",
      providerName: "Ecoterra Costa Rica",
      activityName: "Hanging Bridges Experience",
      activityDetails:
        "The Hanging bridges forest reserve comprises 243 hectares (600 acres) of protected forest. It is ideal for hikers who would like to discover and admire the unique characteristics of the tropical rain forest. On the 3.1-kilometer (1.9 miles) circuit you will go across 15 impressive bridges, some of them suspended over the forest, and others stationary. The bridges have different lengths and heights and were made with the intention of accessing the flora and fauna from different perspectives as you go through the different levels of the forest. From these bridges, you will have great views from the heights of the forest’s canopy making it easier to spot birds and animals. The topography of the terrain is relatively easy with some short inclines, making this tour apt for all types of people. Some of the animals that inhabit this type of forest include three species of monkeys, multiple species of birds, serpents, wild boars, frogs, and more.\nWhat to bring: Hiking shoes or tennis shoes (Closed shoes are mandatory), comfortable clothing, raincoat or poncho, insect repellent, and money for snacks, tips, or souvenirs.\nWe include: Transportation to and from your hotel in air conditioned minivan with Wi-Fi, bilingual naturalist guide, Reserve entrance fee, bottled mineral water, snacks, and insurance policy.\nDeparture: 07:30 hrs. & 13:00 hrs. Arrival: 11:30 hrs. & 17:00 hrs. Duration: 4 hours.",
      meetingPoint: "Brisas Arenal Hotel",
      contacts: {
        address:
          "1.5km west from La Fortuna Waterfall street. La Fortuna, San Carlos, Alajuela. Costa Rica.",
        phone: "+506 4000 1462",
        email: "info@ecoterracostarica.com",
        web: "https://ecoterracostarica.com",
      },
      coordinates: {
        start: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -84.76779,
          latitude: 10.555163,
          speed: 0,
        },
        end: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -84.76779,
          latitude: 10.555163,
          speed: 0,
        },
      },
      links: {},
      booking: {
        totalAmount: "0.00",
        currency: "USD",
        confirmation: "794240",
        cancellation: "",
        details: "Guided tour in English",
      },
      id: "v36fyomcf19mo6i0xje55j8k",
      taleId: "rwkntocyn9q73xuwux5aqd0g",
      created: 1707167500895,
      updated: 1747935114167,
      startDateTime: "2024-02-09T13:50:00.000Z",
      startTimezone: "America/Costa_Rica",
      endDateTime: "2024-02-09T17:00:00.000Z",
      endTimezone: "America/Costa_Rica",
    },
    {
      type: "activity",
      activityType: "walking",
      providerName: "Ecoterra Costa Rica",
      activityName: "Arenal Volcano Expedition: El Silencio Trail",
      activityDetails:
        "We begin the tour by heading to the El Silencio Private Reserve, to get as close to the base of the volcano as possible by vehicle. Next, we will walk approximately 1.5 kilometer along a volcanic stone path with a moderate degree of difficulty, until we reach the viewpoint, the closest point to volcano permitted to access. At the viewpoint, our guide will provide relevant information on the formation of the Arenal Volcano, its periods of activity, as well as unpublished details of its first eruption in 1968.  You can also enjoy the excellent views of the Green Lagoon and Lake Arenal.  On the guide’s indication, we will begin our descent back.\nPart of the trail around the Arenal Volcano features patches of regenerating forest that were originally affected by ancient eruptions. With a little luck, this 1.5 km (1 mi) trail will provide us with the opportunity to see a wide variety of plants and birds that inhabit the rainforest, like toucans, turkeys, orioles and sometimes peccaries and snakes. At the end of the tour our driver will be waiting for us with a delicious snack of tropical fruit before returning to your hotel in La Fortuna.\nIf you wish, you can add a visit the hot springs of your choice (additional cost) where you will enjoy the thermal mineral waters for approximately three hours on your own. Afterwards, we will pick you up to take you back to your hotel in our modern minivans.\nWhat to bring: Hiking or tennis shoes, comfortable clothes, poncho or rain jacket, insect repellent, sunscreen, swimwear and towel (if adding hot springs), and money for snacks, souvenirs, or tips.\nWe include: Transportation from your hotel in comfortable air-conditioned mini vans with Wi-Fi, bilingual naturalist guide, Reserve entrance fee, bottled mineral water, snacks, and insurance policies. Hot spring entrance included if requested at time of reservation.\nDepartures: 7:30 a.m. and 2:30 p.m.\nArrivals: 11:30 a.m. and 6:00 p.m.\nDuration: 4 Hours approx",
      meetingPoint: "Brisas Arenal Hotel",
      contacts: {
        address:
          "1.5km west from La Fortuna Waterfall street. La Fortuna, San Carlos, Alajuela. Costa Rica.",
        phone: "+506 4000 1462",
        email: "info@ecoterracostarica.com",
        web: "https://ecoterracostarica.com",
      },
      coordinates: {
        start: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -84.748221,
          latitude: 10.469307,
          speed: 0,
        },
        end: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -84.748221,
          latitude: 10.469307,
          speed: 0,
        },
      },
      links: {},
      booking: {
        totalAmount: "0.00",
        currency: "USD",
        confirmation: "794245",
        cancellation: "",
        details: "Guided tour in English",
      },
      id: "nkd8hk2g7z43exsjwkl20hw4",
      taleId: "rwkntocyn9q73xuwux5aqd0g",
      created: 1707167500901,
      updated: 1747935114168,
      startDateTime: "2024-02-09T20:50:00.000Z",
      startTimezone: "America/Costa_Rica",
      endDateTime: "2024-02-10T00:00:00.000Z",
      endTimezone: "America/Costa_Rica",
    },
    {
      type: "activity",
      activityType: "walking",
      providerName: "HELICONIAS RAINFOREST HOTEL",
      activityName: "Jungle Night Tour",
      activityDetails:
        "The guide is available for February 7th The entrance fee for Night Tours is $20 per person, so will be $40, starting at 6:30pm until 8:30pm. Pay the guide when you finish the tour, and is important to let you know if you cancel the tour the same day, will be $30 for the cancelation",
      meetingPoint: "",
      contacts: {
        address: "Bijagua, Upala, Alajuela",
        phone: "+506 2466 8483",
        email: "heliconiaslodge@gmail.com",
        web: "https://heliconiascr.com",
      },
      coordinates: {
        start: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -85.0111016,
          latitude: 10.7135858,
          speed: 0,
        },
        end: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -85.0111016,
          latitude: 10.7135858,
          speed: 0,
        },
      },
      links: {
        reservation: "",
        web: "",
      },
      booking: {
        confirmation: "email",
        cancellation: "",
        totalAmount: "20.00",
        currency: "USD",
        details: "Guided tour in English",
      },
      id: "j19po68op2vpd8imj04oyfrf",
      taleId: "rwkntocyn9q73xuwux5aqd0g",
      created: 1707167500907,
      updated: 1747935114168,
      startDateTime: "2024-02-08T00:30:00.000Z",
      startTimezone: "America/Costa_Rica",
      endDateTime: "2024-02-08T02:30:00.000Z",
      endTimezone: "America/Costa_Rica",
    },
    {
      type: "activity",
      activityType: "idea",
      providerName: "Nature",
      activityName: "Ceiba Tree",
      activityDetails: "",
      meetingPoint: "",
      contacts: {
        address: "",
        phone: "",
        email: "",
        web: "",
      },
      coordinates: {
        start: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -84.863152,
          latitude: 10.516914,
          speed: 0,
        },
        end: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -84.863152,
          latitude: 10.516914,
          speed: 0,
        },
      },
      links: {},
      booking: {
        totalAmount: "0.00",
        currency: "USD",
        confirmation: "",
        cancellation: "",
        details: "",
      },
      id: "k0qy7c6plghcuwe7v5z17rni",
      taleId: "rwkntocyn9q73xuwux5aqd0g",
      created: 1707167500913,
      updated: 1747935114168,
      startDateTime: "2024-02-10T18:00:00.000Z",
      startTimezone: "America/Costa_Rica",
      endDateTime: "2024-02-10T07:00:00.000Z",
      endTimezone: "America/Costa_Rica",
    },
    {
      type: "activity",
      activityType: "shopping",
      providerName: "Angel Azul Mercado Natural",
      activityName: "Grocery Shopping",
      activityDetails: "",
      meetingPoint: "",
      contacts: {
        address: "",
        phone: "",
        email: "",
        web: "",
      },
      coordinates: {
        start: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -84.6465119,
          latitude: 10.4722195,
          speed: 0,
        },
        end: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -84.6465119,
          latitude: 10.4722195,
          speed: 0,
        },
      },
      links: {
        reservation: "",
        web: "",
      },
      booking: {
        totalAmount: "0.00",
        currency: "USD",
        confirmation: "",
        cancellation: "",
        details: "",
      },
      id: "lig91h5r07n8cq3mbghsmr50",
      taleId: "rwkntocyn9q73xuwux5aqd0g",
      created: 1707167500919,
      updated: 1747935114169,
      startDateTime: "2024-02-09T00:00:00.000Z",
      startTimezone: "America/Costa_Rica",
      endDateTime: "2024-02-09T00:00:00.000Z",
      endTimezone: "America/Costa_Rica",
    },
    {
      type: "transportation",
      mode: "car",
      terms: "own",
      originCode: "",
      destinationCode: "",
      originName: "Boise ID",
      destinationName: "Bellevue WA",
      carType: "Jeep Grand Cherokee",
      contacts: {
        address: "",
        phone: "",
        email: "",
        web: "",
      },
      coordinates: {
        departure: {
          accuracy: null,
          altitude: null,
          heading: null,
          longitude: -116.1706901,
          latitude: 43.605101,
          speed: 0,
        },
        arrival: {
          accuracy: null,
          altitude: null,
          heading: null,
          longitude: -122.1476542,
          latitude: 47.6348332,
          speed: 0,
        },
      },
      links: {},
      id: "uzzkrmso2f2m3ro4b8g26u44",
      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      created: 1749086632765,
      updated: 1749086632765,
      departureDateTime: "2025-06-07T14:00:00.000Z",
      departureTimezone: "America/Boise",
      arrivalDateTime: "2025-06-07T23:00:00.000Z",
      arrivalTimezone: "America/Los_Angeles",
    },
    {
      type: "transportation",
      mode: "car",
      terms: "own",
      originCode: "",
      destinationCode: "",
      originName: "Bellevue WA",
      destinationName: "Anacortes WA",
      carType: "Jeep Grand Cherokee",
      contacts: {
        address: "",
        phone: "",
        email: "",
        web: "",
      },
      coordinates: {
        departure: {
          accuracy: null,
          altitude: null,
          heading: null,
          longitude: -122.1476542,
          latitude: 47.6348332,
          speed: 0,
        },
        arrival: {
          accuracy: null,
          altitude: null,
          heading: null,
          longitude: -122.6167145,
          latitude: 48.485338,
          speed: 0,
        },
      },
      links: {},
      id: "ny7tiz4pqx7j8mx6io87nzye",
      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      created: 1749086632766,
      updated: 1749086632766,
      departureDateTime: "2025-06-08T19:00:00.000Z",
      departureTimezone: "America/Los_Angeles",
      arrivalDateTime: "2025-06-08T20:30:00.000Z",
      arrivalTimezone: "America/Los_Angeles",
    },
    {
      type: "transportation",
      mode: "boat",
      terms: "hire",
      confirmation: "52817486",
      providerCode: "",
      providerName: "Washington State Ferries",
      originCode: "",
      destinationCode: "",
      originName: "Anacortes WA",
      destinationName: "Orcas Island WA",
      contacts: {
        address: "2100 Ferry Terminal Road, Anacortes, WA 98221",
        phone: "1-888-808-7977",
        email: "",
        web: "",
      },
      coordinates: {
        departure: {
          accuracy: null,
          altitude: null,
          heading: null,
          longitude: -122.6167145,
          latitude: 48.485338,
          speed: 0,
        },
        arrival: {
          accuracy: null,
          altitude: null,
          heading: null,
          longitude: -122.946723,
          latitude: 48.5978006,
          speed: 0,
        },
      },
      links: {},
      id: "wv8th7nu2g7j1a75boy2vqr2",
      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      created: 1749086632767,
      updated: 1749086632767,
      departureDateTime: "2025-06-08T22:20:00.000Z",
      departureTimezone: "America/Los_Angeles",
      arrivalDateTime: "2025-06-08T23:50:00.000Z",
      arrivalTimezone: "America/Los_Angels",
    },
    {
      type: "transportation",
      mode: "boat",
      terms: "hire",
      confirmation: "52817490",
      providerCode: "",
      providerName: "Washington State Ferries",
      originCode: "",
      destinationCode: "",
      originName: "Orcas Island WA",
      destinationName: "Anacortes WA",
      contacts: {
        address: "8368 Orcas Road, Orcas Island, WA 98280",
        phone: "1-888-808-7977",
        email: "",
        web: "",
      },
      coordinates: {
        departure: {
          accuracy: null,
          altitude: null,
          heading: null,
          longitude: -122.946723,
          latitude: 48.5978006,
          speed: 0,
        },
        arrival: {
          accuracy: null,
          altitude: null,
          heading: null,
          longitude: -122.6167145,
          latitude: 48.485338,
          speed: 0,
        },
      },
      links: {},
      id: "tjdj2f0tekqrcrplwpa0wbx1",
      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      created: 1749086632767,
      updated: 1749086632767,
      departureDateTime: "2025-06-14T23:50:00.000Z",
      departureTimezone: "America/Los_Angeles",
      arrivalDateTime: "2025-06-15T00:50:00.000Z",
      arrivalTimezone: "America/Los_Angeles",
    },
    {
      type: "accommodation",
      spaceType: "hotel",
      terms: "rent",
      providerName: "Marriott Residence Inn",
      contacts: {
        address: "14595 NE 29th Place Bellevue, Washington 98007 USA",
        phone: "+1-425-869-6548",
        email: "",
        web: "",
      },
      coordinates: {
        accuracy: null,
        altitude: null,
        heading: null,
        longitude: -122.1476542,
        latitude: 47.6348332,
        speed: 0,
      },
      links: {
        reservation: "",
      },
      booking: {
        confirmation: "93664007",
        totalAmount: "0.00",
        currency: "USD",
        cancellation:
          "cancel your reservation for no charge before 11:59 PM local hotel time on Thursday, June 5, 2025",
        details: "",
        roomType: "Guest Room, 1 King",
        specialRequests: "",
        services: ["laundry", "breakfast", "parking $12/daily", "refrigerator"],
      },
      id: "azjcakgjz1bjg5aprz6jc24j",
      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      created: 1749086632767,
      updated: 1749086632767,
      checkInDateTime: "2025-06-07T23:00:00.000Z",
      checkOutDateTime: "2025-06-08T18:00:00.000Z",
      timezone: "America/Los_Angeles",
    },
    {
      type: "accommodation",
      spaceType: "airbnb",
      terms: "rent",
      providerName: "Home in Eastsound",
      contacts: {
        address: "1784 Raccoon Point Road Eastsound, WA 98245",
        phone: "",
        email: "",
        web: "https://www.airbnb.com/trips/v1/reservation-details/ro/RESERVATION2_CHECKIN/HM8ZXSZMYP",
      },
      coordinates: {
        accuracy: null,
        altitude: null,
        heading: null,
        longitude: -122.8402628,
        latitude: 48.695886,
        speed: 0,
      },
      links: {
        reservation: "",
      },
      booking: {
        confirmation: "HM8ZXSZMYP",
        totalAmount: "0.00",
        currency: "USD",
        cancellation:
          "Free cancellation before 3:00PM on Jun 3. Cancel before check-in at 3:00PM on Jun 8 for a partial refund.",
        details: "",
        roomType: "",
        specialRequests: "",
        services: [],
      },
      id: "d33usnuhtsfjqpiab4vxiate",
      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      created: 1749086632768,
      updated: 1749086632768,
      checkInDateTime: "2025-06-08T22:00:00.000Z",
      checkOutDateTime: "2025-06-14T19:00:00.000Z",
      timezone: "America/Los_Angeles",
    },
    {
      type: "accommodation",
      spaceType: "hotel",
      terms: "rent",
      providerName: "Leavenworth Village Inn",
      contacts: {
        address: "1016 Commercial St. Leavenworth, WA 98826",
        phone: "509.548.6620",
        email: "guestservices@leavenworthvillageinn.com",
        web: "http://www.leavenworthvillageinn.com",
      },
      coordinates: {
        accuracy: null,
        altitude: null,
        heading: null,
        longitude: -120.6611178,
        latitude: 47.5962534,
        speed: 0,
      },
      links: {
        reservation: "",
      },
      booking: {
        confirmation: "642-694-451",
        totalAmount: "0.00",
        currency: "USD",
        cancellation: "",
        details:
          "If you believe you may arrive after 8pm, please let us know so we can arrange an after hours check-in for you and please verify we have your correct mobile number on file to send you check-in instructions.",
        roomType: "Guest Room, 1 Queen",
        specialRequests: "",
        services: ["laundry", "breakfast", "parking $12/daily", "refrigerator"],
      },
      id: "fa3gbhst0rdwe1jst1ujwxho",
      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      created: 1749086632767,
      updated: 1749086632767,
      checkInDateTime: "2025-06-15T23:00:00.000Z",
      checkOutDateTime: "2025-06-16T18:00:00.000Z",
      timezone: "America/Los_Angeles",
    },
    {
      type: "activity",
      activityType: "boating",
      providerName: "Dear Harbor Charters",
      activityName: "Whale Watch - Deer Harbor",
      activityDetails:
        "Our Deer Harbor Marina office is located in the floating barge . Simply walk down the main pier (cafe on end of main pier) and take the ramp to the right, down to the floating barge. Please make sure you check in with your naturalist 15-30 minutes prior to your tour departure time. Boarding begins 10 minutes prior to departure.\n\nWhat to Bring:\n\n-Warm jacket & long pants (rain- or windproof recommended in spring and fall)\n-Hat, sunscreen & sunglasses\n-Snacks, lunch, and water\n\nOnboard Amenities:\n\n-Blankets\n-Binoculars\n-Restroom\n-Free tour photos (taken during your trip)",
      meetingPoint: "5164 Deer Harbor Rd Deer Harbor, WA 98243",
      contacts: {
        address: "5164 Deer Harbor Rd Deer Harbor, WA 98243",
        phone: "(360) 376-5989",
        email: "info@deerharborcharters.com",
        web: "https://deerharborcharters.com",
      },
      coordinates: {
        start: {
          accuracy: null,
          altitude: null,
          heading: null,
          longitude: -123.0048354,
          latitude: 48.6200085,
          speed: 0,
        },
        end: {
          accuracy: null,
          altitude: null,
          heading: null,
          longitude: -123.0048354,
          latitude: 48.6200085,
          speed: 0,
        },
      },
      links: {},
      booking: {
        totalAmount: "135.00",
        currency: "USD",
        confirmation: "288684344",
        cancellation: "",
        details: "",
      },
      id: "uxugq19kheenbgchlp2qcoo7",
      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      created: 1749086632768,
      updated: 1749086632768,
      startDateTime: "2025-06-10T18:00:00.000Z",
      startTimezone: "America/Los_Angeles",
      endDateTime: "2025-06-10T21:30:00.000Z",
      endTimezone: "America/Los_Angeles",
    },
    {
      type: "accommodation",
      spaceType: "airbnb",
      terms: "rent",
      providerName: "Baker View Getaway",
      contacts: {
        address: "",
        phone: "",
        email: "",
        web: "https://www.airbnb.com/trips/v1/reservation-details/ro/RESERVATION2_CHECKIN/HMX3DPJAKM",
      },
      coordinates: {
        accuracy: null,
        altitude: null,
        heading: null,
        longitude: -122.6808309,
        latitude: 48.5065109,
        speed: 0,
      },
      links: {
        reservation:
          "https://www.airbnb.com/trips/v1/reservation-details/ro/RESERVATION2_CHECKIN/HMX3DPJAKM",
      },
      booking: {
        confirmation: "HMX3DPJAKM",
        totalAmount: "166.00",
        currency: "USD",
        cancellation:
          "Free cancellation before 3:00 PM on Jun 13. After that, the reservation is non-refundable.",
        details: "",
        roomType: "",
        specialRequests: "",
        services: [],
      },
      id: "ij4zt55ls828h85a8bz1znah",
      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      created: 1749086632769,
      updated: 1749086632769,
      checkInDateTime: "2025-06-14T22:00:00.000Z",
      checkOutDateTime: "2025-06-15T20:00:00.000Z",
      timezone: "America/Los_Angeles",
    },
    {
      type: "transportation",
      mode: "car",
      terms: "own",
      originCode: "",
      destinationCode: "",
      originName: "Anacortes WA",
      destinationName: "Leavenworth WA",
      carType: "Jeep Grand Cherokee",
      contacts: {
        address: "",
        phone: "",
        email: "",
        web: "",
      },
      coordinates: {
        departure: {
          accuracy: null,
          altitude: null,
          heading: null,
          longitude: -122.6167145,
          latitude: 48.485338,
          speed: 0,
        },
        arrival: {
          accuracy: null,
          altitude: null,
          heading: null,
          longitude: -120.6611178,
          latitude: 47.5962534,
          speed: 0,
        },
      },
      links: {},
      id: "nijbffn311dhysmra3f7qo15",
      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      created: 1749086632766,
      updated: 1749086632766,
      departureDateTime: "2025-06-15T15:00:00.000Z",
      departureTimezone: "America/Los_Angeles",
      arrivalDateTime: "2025-06-15T18:00:00.000Z",
      arrivalTimezone: "America/Los_Angeles",
    },
    {
      type: "transportation",
      mode: "car",
      terms: "own",
      originCode: "",
      destinationCode: "",
      originName: "Leavenworth WA",
      destinationName: "Boise ID",
      carType: "Jeep Grand Cherokee",
      contacts: {
        address: "",
        phone: "",
        email: "",
        web: "",
      },
      coordinates: {
        departure: {
          accuracy: null,
          altitude: null,
          heading: null,
          longitude: -120.6611178,
          latitude: 47.5962534,
          speed: 0,
        },
        arrival: {
          accuracy: null,
          altitude: null,
          heading: null,
          longitude: -116.1706901,
          latitude: 43.605101,
          speed: 0,
        },
      },
      links: {},
      id: "e5f96znbo2jxmtw0xro0eom5",
      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      created: 1749086632766,
      updated: 1749086632766,
      departureDateTime: "2025-06-16T17:00:00.000Z",
      departureTimezone: "America/Los_Angeles",
      arrivalDateTime: "2025-06-17T01:00:00.000Z",
      arrivalTimezone: "America/Boise",
    },
    {
      type: "activity",
      activityType: "idea",
      providerName: "The Barnacle",
      activityName: "Drinks",
      activityDetails: "",
      meetingPoint: "",
      contacts: {
        address: "",
        phone: "",
        email: "",
        web: "https://www.thebarnacle.com",
      },
      coordinates: {
        start: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -122.9083419,
          latitude: 48.6963399,
          speed: 0,
        },
        end: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -122.9083419,
          latitude: 48.6963399,
          speed: 0,
        },
      },
      links: {},
      booking: {
        totalAmount: 0,
        currency: "USD",
        confirmation: "",
        cancellation: "",
        details: "",
      },
      id: "hpeed5ui13voftwo4k75vp2s",
      taleId: "v8gj7ejto9iujvkiob8q3g8u",
      created: 1749086632769,
      updated: 1749086632769,
      startDateTime: "2025-06-13T00:00:00.000Z",
      startTimezone: "America/Los_Angeles",
      endDateTime: "2025-06-14T05:00:00.000Z",
      endTimezone: "America/Los_Angeles",
    },
    {
      type: "accommodation",
      spaceType: "condo",
      terms: "guest",
      providerName: "Adagio",
      contacts: {
        address: "",
        phone: "",
        email: "",
        web: "https://yourfriendatthebeach.com/",
      },
      coordinates: {
        accuracy: null,
        altitude: null,
        heading: null,
        longitude: -86.2058921,
        latitude: 30.3393401,
        speed: 0,
      },
      links: {
        reservation:
          "https://yourfriendatthebeach.com/reservation-summary/?start_date=2025-10-25&end_date=2025-11-01&id=D2IRO&unit_code=1907-229554&total=3591.84",
      },
      booking: {
        confirmation: "D2IRO",
        totalAmount: "0.00",
        currency: "USD",
        cancellation: "",
        details: "Reservation under Chip and Janice",
        roomType: "A404",
        specialRequests: "",
        services: [],
      },
      id: "li02cyy6adnih44mo55x7n7t",
      taleId: "a3y4oi0kssjimi59mba751r1",
      created: null,
      updated: null,
      checkInDateTime: "2025-10-25T21:00:00.000Z",
      checkOutDateTime: "2025-11-01T15:00:00.000Z",
      timezone: "America/Chicago",
    },
    {
      type: "transportation",
      mode: "airplane",
      terms: "hire",
      confirmation: "G7F8Y2",
      providerCode: "DL2607",
      providerName: "Delta",
      originCode: "SLC",
      destinationCode: "BOI",
      originName: "Salt Lake City, UT",
      destinationName: "Boise, ID",
      contacts: {
        address: "",
        phone: "",
        email: "",
        web: "",
      },
      coordinates: {
        departure: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -111.891045,
          latitude: 40.76078,
          speed: 0,
        },
        arrival: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -116.202316,
          latitude: 43.615021,
          speed: 0,
        },
      },
      links: {},
      id: "htj5up0xoqokh5x3ebrt7klw",
      taleId: "a3y4oi0kssjimi59mba751r1",
      created: null,
      updated: null,
      departureDateTime: "2025-11-02T03:35:00.000Z",
      departureTimezone: "America/Boise",
      arrivalDateTime: "2025-11-02T04:50:00.000Z",
      arrivalTimezone: "America/Boise",
    },
    {
      type: "transportation",
      mode: "airplane",
      terms: "hire",
      confirmation: "G7F8Y2",
      providerCode: "DL2607",
      providerName: "Delta",
      originCode: "MSP",
      destinationCode: "SLC",
      originName: "Minneapolis/St. Paul, MN",
      destinationName: "Salt Lake City, UT",
      contacts: {
        address: "",
        phone: "",
        email: "",
        web: "",
      },
      coordinates: {
        departure: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -93.265015,
          latitude: 44.977753,
          speed: 0,
        },
        arrival: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -111.891045,
          latitude: 40.76078,
          speed: 0,
        },
      },
      links: {},
      id: "ag3vuijvwzf9vljrpiejtlow",
      taleId: "a3y4oi0kssjimi59mba751r1",
      created: null,
      updated: null,
      departureDateTime: "2025-11-01T23:10:00.000Z",
      departureTimezone: "America/Chicago",
      arrivalDateTime: "2025-11-02T02:00:00.000Z",
      arrivalTimezone: "America/Boise",
    },
    {
      type: "transportation",
      mode: "airplane",
      terms: "hire",
      confirmation: "G7F8Y2",
      providerCode: "DL2462",
      providerName: "Delta",
      originCode: "VPS",
      destinationCode: "MSP",
      originName: "Destin-Fort Walton, FL",
      destinationName: "Minneapolis/St. Paul, MN",
      contacts: {
        address: "",
        phone: "",
        email: "",
        web: "",
      },
      coordinates: {
        departure: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -86.633772,
          latitude: 30.421922,
          speed: 0,
        },
        arrival: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -93.265015,
          latitude: 44.977753,
          speed: 0,
        },
      },
      links: {},
      id: "rowbelm9l9nqdj7alv62axeb",
      taleId: "a3y4oi0kssjimi59mba751r1",
      created: null,
      updated: null,
      departureDateTime: "2025-11-01T17:55:00.000Z",
      departureTimezone: "America/Chicago",
      arrivalDateTime: "2025-11-01T20:50:00.000Z",
      arrivalTimezone: "America/Chicago",
    },
    {
      type: "transportation",
      mode: "airplane",
      terms: "hire",
      confirmation: "G7F8Y2",
      providerCode: "DL3083",
      providerName: "Delta",
      originCode: "ATL",
      destinationCode: "VPS",
      originName: "Atlanta, GA",
      destinationName: "Destin-Fort Walton, FL",
      contacts: {
        address: "",
        phone: "",
        email: "",
        web: "",
      },
      coordinates: {
        departure: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -84.387985,
          latitude: 33.748997,
          speed: 0,
        },
        arrival: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -86.633772,
          latitude: 30.421922,
          speed: 0,
        },
      },
      links: {},
      id: "b2ymifgboex60i3ldysp3pro",
      taleId: "a3y4oi0kssjimi59mba751r1",
      created: null,
      updated: null,
      departureDateTime: "2025-10-26T01:50:00.000Z",
      departureTimezone: "America/New_York",
      arrivalDateTime: "2025-10-26T02:58:00.000Z",
      arrivalTimezone: "America/Chicago",
    },
    {
      type: "transportation",
      mode: "airplane",
      terms: "hire",
      confirmation: "G7F8Y2",
      providerCode: "DL0874",
      providerName: "Delta",
      originCode: "BOI",
      destinationCode: "ATL",
      originName: "Boise, ID",
      destinationName: "Atlanta, GA",
      contacts: {
        address: "",
        phone: "",
        email: "",
        web: "",
      },
      coordinates: {
        departure: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -116.202316,
          latitude: 43.615021,
          speed: 0,
        },
        arrival: {
          accuracy: "",
          altitude: "",
          heading: null,
          longitude: -84.387985,
          latitude: 33.748997,
          speed: 0,
        },
      },
      links: {},
      id: "y7wzh8dyg3mse1x2bqyhie21",
      taleId: "a3y4oi0kssjimi59mba751r1",
      created: null,
      updated: null,
      departureDateTime: "2025-10-25T19:39:00.000Z",
      departureTimezone: "America/Boise",
      arrivalDateTime: "2025-10-25T23:47:00.000Z",
      arrivalTimezone: "America/New_York",
    },
    {
      type: "activity",
      activityType: "boating",
      providerName: "Destin Pontoon Charters",
      activityName: "Private Guided Sunset and Eco Tour",
      activityDetails:
        "Parking NOT included!  Our cruise is a private tour to see Destin's sunset as you want to see it. We can look for Dolphins, or watch the sunset from classic Destin Locations Crab Island and Norriego Point. Our boat includes a cooler with bottled water and ice so you can bring your favorite drink to relax with. Bluetooth speakers are on board as well so you can be the DJ!",
      meetingPoint: "314 Harbor Blvd, Destin, FL 32541, USA",
      contacts: {
        address: "",
        phone: "18505176011",
        email: "contact@destinpontooncharters.com",
        web: "https://www.destinpontooncharters.com",
      },
      coordinates: {
        start: {
          accuracy: null,
          altitude: null,
          heading: null,
          longitude: -86.504256,
          latitude: 30.393297,
          speed: 0,
        },
        end: {
          accuracy: null,
          altitude: null,
          heading: null,
          longitude: -86.504256,
          latitude: 30.393297,
          speed: 0,
        },
      },
      links: {},
      booking: {
        totalAmount: "0.00",
        currency: "USD",
        confirmation: "317836245",
        cancellation: "",
        details: "",
      },
      id: "lfqkh3ju5nfyg5nr23qce98r",
      taleId: "a3y4oi0kssjimi59mba751r1",
      created: 0,
      updated: 0,
      startDateTime: "2025-10-29T22:00:00.000Z",
      startTimezone: "America/Chicago",
      endDateTime: "2025-10-29T23:30:00.000Z",
      endTimezone: "America/Chicago",
    },
  ],
  /**
   * Hooks
   *
   * - hooks are for data objects and do not apply to storeObjects
   */
  hooks: {
    // Will run for *data rows only* (never for the storeObject/meta row)
    // transform: (obj, ctx) => {
    //   console.debug(
    //     `[settings@${ctx.stepVersion}] transform`,
    //     Object.keys(obj)
    //   );
    //   // Only prune in *this* version, e.g. when you removed fields
    //   ctx.utils.keyPruning(obj, ctx.model);
    //   return obj;
    // },
    // afterPopulate: async ({ db, store, version, cfg }) => {},
    // beforePopulate: async ({ db, store, version, cfg }) => {
    // const generate = cfg?.dataPolicies?.id?.generate || null;
    // const shouldGenerateAll = generate === "all";
    // const shouldGenerateInvalid = generate === "invalid";
    // const data = cfg.data;
    // const dataLength = data.length;
    // for (let i = 0; i < dataLength; i++) {
    //   const rec = data[i];
    //   if (rec.id === cfg.store) continue;
    //   //create cuid2
    //   if (shouldGenerateAll) {
    //     rec.id = createId();
    //   } else if (shouldGenerateInvalid && !isValidCuid2(rec.id)) {
    //     rec.id = createId();
    //   }
    //   // departure/arrival
    //   // start/end
    //   // pickup/return
    //   // checkin/checkout
    //   // convert to utc
    //   if (rec.departureDateTime) {
    //     const utcIso = convertIsoOffsetToUTC(rec.departureDateTime);
    //     rec.departureDateTime = utcIso;
    //   }
    //   if (rec.arrivalDateTime) {
    //     const utcIso = convertIsoOffsetToUTC(rec.arrivalDateTime);
    //     rec.arrivalDateTime = utcIso;
    //   }
    //   if (rec.startDateTime) {
    //     const utcIso = convertIsoOffsetToUTC(rec.startDateTime);
    //     rec.startDateTime = utcIso;
    //   }
    //   if (rec.endDateTime) {
    //     const utcIso = convertIsoOffsetToUTC(rec.endDateTime);
    //     rec.endDateTime = utcIso;
    //   }
    //   if (rec.pickupDateTime) {
    //     const utcIso = convertIsoOffsetToUTC(rec.pickupDateTime);
    //     rec.pickupDateTime = utcIso;
    //   }
    //   if (rec.returnDateTime) {
    //     const utcIso = convertIsoOffsetToUTC(rec.returnDateTime);
    //     rec.returnDateTime = utcIso;
    //   }
    //   if (rec.checkInDateTime) {
    //     const utcIso = convertIsoOffsetToUTC(rec.checkInDateTime);
    //     rec.checkInDateTime = utcIso;
    //   }
    //   if (rec.checkOutDateTime) {
    //     const utcIso = convertIsoOffsetToUTC(rec.checkOutDateTime);
    //     rec.checkOutDateTime = utcIso;
    //   }
    // }
    //   const generate = cfg?.dataPolicies?.id?.generate || null;
    //   const shouldGenerateAll = generate === "all";
    //   const shouldGenerateInvalid = generate === "invalid";
    // const data = cfg.data;
    // const dataLength = data.length;
    // for (let i = 0; i < dataLength; i++) {
    //   const rec = data[i];
    //   if (rec.id === cfg.store) continue;
    //   // fix ids
    //   const generate = cfg?.dataPolicies?.id?.generate || null;
    //   const shouldGenerateAll = generate === "all";
    //   const shouldGenerateInvalid = generate === "invalid";
    //   const data = cfg.data;
    //   const dataLength = data.length;
    //   for (let i = 0; i < dataLength; i++) {
    //     const rec = data[i];
    //     if (rec.id === cfg.store) continue;
    //     if (shouldGenerateAll) {
    //       rec.id = createId();
    //     } else if (shouldGenerateInvalid && !isValidCuid2(rec.id)) {
    //       rec.id = createId();
    //     }
    //   }
    //date fixes: complete
    // if (rec.departureDate && rec.arrivalDate) {
    //   let depAbbr = "UTC";
    //   let arrAbbr = "UTC";
    //   if (rec.departureTz) depAbbr = rec.departureTz;
    //   else {
    //     if (rec.origin && rec.origin.includes("WA")) depAbbr = "PDT";
    //     else if (rec.origin && rec.origin.includes("LIR")) depAbbr = "CST";
    //     else depAbbr = "MDT";
    //   }
    //   if (rec.arrivalTz) arrAbbr = rec.arrivalTz;
    //   else {
    //     if (rec.destination && rec.destination.includes("WA"))
    //       arrAbbr = "PDT";
    //     else if (rec.destination && rec.destination.includes("LIR"))
    //       arrAbbr = "CST";
    //     else arrAbbr = "MST";
    //   }
    //   const depOff = timezoneOffsets[depAbbr];
    //   const arrOff = timezoneOffsets[arrAbbr];
    //   //2025-11-15T12:28:00
    //   rec.departure = rec.departureDate + "T" + rec.departureTime + depOff;
    //   rec.arrival = rec.arrivalDate + "T" + rec.arrivalTime + arrOff;
    //   // remove old keys
    //   delete rec.departureDate;
    //   delete rec.arrivalDate;
    //   delete rec.departureTime;
    //   delete rec.arrivalTime;
    //   delete rec.departureTz;
    //   delete rec.arrivalTz;
    // } else if (rec.checkInDate && rec.checkOutDate) {
    //   let tzAbbr = "UTC";
    //   if (rec.timeZone) tzAbbr = rec.timeZone;
    //   const inOff = timezoneOffsets[tzAbbr];
    //   const outOff = timezoneOffsets[tzAbbr];
    //   //2025-11-15T12:28:00
    //   rec.checkIn = rec.checkInDate + "T" + rec.checkInTime + inOff;
    //   rec.checkOut = rec.checkOutDate + "T" + rec.checkOutTime + outOff;
    //   // remove old keys
    //   delete rec.checkOutDate;
    //   delete rec.checkOutTime;
    //   delete rec.checkInDate;
    //   delete rec.checkInTime;
    //   delete rec.timeZone;
    // } else if (rec.startDate && rec.startDate) {
    //   let tzAbbr = "UTC";
    //   if (rec.timeZone) tzAbbr = rec.timeZone;
    //   const startOff = timezoneOffsets[tzAbbr];
    //   const endOff = timezoneOffsets[tzAbbr];
    //   //2025-11-15T12:28:00
    //   rec.start = rec.startDate + "T" + rec.startTime + startOff;
    //   rec.end = rec.endDate + "T" + rec.endTime + endOff;
    //   // remove old keys
    //   delete rec.startDate;
    //   delete rec.startTime;
    //   delete rec.endDate;
    //   delete rec.endTime;
    //   delete rec.timeZone;
    // }
    // remove passengers, guests, participants
    // if (rec.passengers)
    // delete rec.passengers;
    // if (rec.guests)
    // delete rec.guests;
    // if (rec.participants)
    // delete rec.participants;
    // convertDateStringsToTimestamps(rec);
    //   console.debug(data);
    // },
  },
};

/* eslint-enable import/no-anonymous-default-export */

//       departureDate: "2024-02-06",
//       arrivalDate: "2024-02-06",
//       departureTime: "06:30",
//       arrivalTime: "08:37",

//       departureTz: "CST",
//       arrivalTz: "MST",

// no timezone key

//       checkInDate: "2024-02-06",
//       checkOutDate: "2024-02-08",
//       checkInTime: "14:00",
//       checkOutTime: "11:00",
//       timeZone: "CST",

//         pickupDate: "2024-02-06",
//         returnDate: "2024-02-16",
//         pickupTime: "16:30",
//         returnTime: "11:30",
// no timezone

//       startDate: "2024-02-08",
//       endDate: "2024-02-08",
//       startTime: "11:00",
//       endTime: "15:30",
//       timeZone: "CST",
